import React, { useState, useEffect, useRef } from 'react'
import Button from 'aldoo-ra/Button'
import {
  Bars3BottomLeftIcon,
  Bars3Icon,
  Bars3BottomRightIcon,
  QueueListIcon
} from '@heroicons/react/24/outline'

const AlignmentPicker = ({ onAlignmentSelect, onClose, savedSelection }) => {
  const [selectedAlignment, setSelectedAlignment] = useState(null)
  const containerRef = useRef(null)

  // Alignment options with icons and labels
  const alignmentOptions = [
    {
      type: 'left',
      label: 'Align Left',
      icon: Bars3BottomLeftIcon,
      className: 'ae-align-left'
    },
    {
      type: 'center', 
      label: 'Align Center',
      icon: Bars3Icon,
      className: 'ae-align-center'
    },
    {
      type: 'right',
      label: 'Align Right', 
      icon: Bars3BottomRightIcon,
      className: 'ae-align-right'
    },
    {
      type: 'justify',
      label: 'Justify',
      icon: QueueListIcon,
      className: 'ae-align-justify'
    }
  ]

  // Detect current alignment from selection
  useEffect(() => {
    if (savedSelection?.range) {
      try {
        const range = savedSelection.range
        let element = range.commonAncestorContainer

        // If it's a text node, get the parent element
        if (element.nodeType === Node.TEXT_NODE) {
          element = element.parentElement
        }

        // Check if we're dealing with an image first
        let targetImage = null

        // First, check if there's a transform gizmo active (indicates selected image)
        const activeGizmo = document.querySelector('.ae-transform-gizmo')
        if (activeGizmo) {
          // Find the image that the gizmo is positioned on
          const allImages = document.querySelectorAll('.editor img, .editor-image')
          for (const img of allImages) {
            const imgRect = img.getBoundingClientRect()
            const gizmoRect = activeGizmo.getBoundingClientRect()
            
            // Check if gizmo is positioned on this image (with some tolerance)
            if (Math.abs(imgRect.left - gizmoRect.left) < 5 && 
                Math.abs(imgRect.top - gizmoRect.top) < 5) {
              targetImage = img
              break
            }
          }
        }

        // If no gizmo-based detection, fall back to selection-based detection
        if (!targetImage) {
          if (element.tagName === 'IMG') {
            targetImage = element
          } else {
            // Look for an image within the selection
            targetImage = element.querySelector('img')
            if (!targetImage && range.startContainer.nodeType === Node.ELEMENT_NODE) {
              const startElement = range.startContainer.childNodes[range.startOffset]
              if (startElement && startElement.tagName === 'IMG') {
                targetImage = startElement
              }
            }
          }
        }

        // If we found an image, check its alignment styles (images use inline styles)
        if (targetImage) {
          const computedStyle = window.getComputedStyle(targetImage)
          const float = computedStyle.float || targetImage.style.float
          const margin = computedStyle.margin || targetImage.style.margin
          const display = computedStyle.display || targetImage.style.display
          
          if (float === 'left') {
            setSelectedAlignment('left')
            return
          } else if (float === 'right') {
            setSelectedAlignment('right')
            return
          } else if ((float === 'none' || !float) && (display === 'block') && 
                     (margin.includes('auto') || targetImage.style.margin === '1rem auto')) {
            setSelectedAlignment('center')
            return
          }
          
          // Check for class-based alignment as fallback
          if (targetImage.classList?.contains('ae-align-left')) {
            setSelectedAlignment('left')
            return
          } else if (targetImage.classList?.contains('ae-align-center')) {
            setSelectedAlignment('center')
            return
          } else if (targetImage.classList?.contains('ae-align-right')) {
            setSelectedAlignment('right')
            return
          } else if (targetImage.classList?.contains('ae-align-justify')) {
            setSelectedAlignment('justify')
            return
          }
        }

        // Walk up the DOM to find an element with alignment (for text)
        while (element && element !== document.body) {
          const computedStyle = window.getComputedStyle(element)
          const textAlign = computedStyle.textAlign

          // Check for CSS classes
          const hasLeftClass = element.classList?.contains('ae-align-left')
          const hasCenterClass = element.classList?.contains('ae-align-center') 
          const hasRightClass = element.classList?.contains('ae-align-right')
          const hasJustifyClass = element.classList?.contains('ae-align-justify')

          if (hasLeftClass || textAlign === 'left') {
            setSelectedAlignment('left')
            break
          } else if (hasCenterClass || textAlign === 'center') {
            setSelectedAlignment('center')
            break
          } else if (hasRightClass || textAlign === 'right') {
            setSelectedAlignment('right')
            break
          } else if (hasJustifyClass || textAlign === 'justify') {
            setSelectedAlignment('justify')
            break
          }

          element = element.parentElement
        }
      } catch (error) {
        console.warn('Error detecting current alignment:', error)
      }
    }
  }, [savedSelection])

  const handleAlignmentClick = (alignmentType) => {
    setSelectedAlignment(alignmentType)
    onAlignmentSelect(alignmentType)
  }

  const handleRemoveAlignment = () => {
    setSelectedAlignment(null)
    onAlignmentSelect('remove')
  }

  return (
    <div
      ref={containerRef}
      className="min-w-[240px] bg-admin_paper border border-admin_bordered rounded-lg shadow-lg p-4"
      data-alignment-picker="true"
      style={{ 
        userSelect: 'none',
        pointerEvents: 'auto',
        position: 'relative',
        zIndex: 9999
      }}
    >
      {/* Header */}
      <div className="flex items-center mb-3">
        <h3 className="text-sm font-semibold text-admin_text">
          Alignment
        </h3>
      </div>

      {/* Current Selection Info */}
      {selectedAlignment && (
        <div className="mb-3 p-2 bg-admin_info_bg rounded text-xs text-admin_info">
          Current: {alignmentOptions.find(opt => opt.type === selectedAlignment)?.label || 'Unknown'}
        </div>
      )}

      {/* Alignment Options */}
      <div className="space-y-2 mb-3">
        <div className="text-xs font-medium text-admin_text_secondary mb-2">
          Choose Alignment:
        </div>

        <div className="grid grid-cols-2 gap-2">
          {alignmentOptions.map((option) => {
            const IconComponent = option.icon
            const isSelected = selectedAlignment === option.type
            
            return (
              <Button
                key={option.type}
                variant={isSelected ? "admin_primary" : "admin_secondary"}
                className={`flex items-center justify-center gap-2 p-3 text-sm transition-all ${
                  isSelected ? 'ring-2 ring-admin_primary' : ''
                }`}
                onClick={() => handleAlignmentClick(option.type)}
                onMouseDown={(e) => e.preventDefault()}
                title={option.label}
              >
                <IconComponent className="w-4 h-4" />
                <span className="hidden sm:inline">
                  {option.type.charAt(0).toUpperCase() + option.type.slice(1)}
                </span>
              </Button>
            )
          })}
        </div>
      </div>

      {/* Remove Alignment Button */}
      {selectedAlignment && (
        <div className="pt-2 border-t border-admin_bordered">
          <Button
            variant="admin_text"
            className="w-full flex items-center justify-center gap-2 text-sm text-admin_error/80 hover:text-admin_error"
            onClick={handleRemoveAlignment}
            onMouseDown={(e) => e.preventDefault()}
          >
            <span>Remove Alignment</span>
          </Button>
        </div>
      )}

      {/* Instructions */}
      <div className="mt-3 pt-2 border-t border-admin_bordered">
        <p className="text-xs text-admin_text_secondary">
          Select text or an image, then choose an alignment option.
        </p>
      </div>
    </div>
  )
}

export default AlignmentPicker